
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, Utensils, Heart, Wine, Dog } from 'lucide-react';
import { Terrace } from '@/types/terrace';
import SunStatusIndicator from '@/components/SunStatusIndicator';

interface TerraceCardProps {
  terrace: Terrace;
  onFavorite?: (terraceId: string) => void;
  isFavorited?: boolean;
}

const TerraceCard = ({ terrace, onFavorite, isFavorited }: TerraceCardProps) => {
  const handleFavorite = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onFavorite?.(terrace.id);
  };

  return (
    <Link to={`/terrace/${terrace.slug}`} className="block">
      <Card className="h-full hover:shadow-md transition-shadow cursor-pointer group border-peach-orange/20">
        <CardHeader className="pb-3 p-3 sm:p-4 sm:pb-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-base sm:text-lg font-semibold text-sunset-dark group-hover:text-peach-orange transition-colors line-clamp-2">
                {terrace.name}
              </CardTitle>
              <div className="flex items-center text-xs sm:text-sm text-sunset-medium mt-1">
                <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">{terrace.address}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <SunStatusIndicator 
                status={terrace.sunStatus} 
                showLabel={false}
                className="text-xs sm:text-sm"
              />
              {onFavorite && (
                <button
                  onClick={handleFavorite}
                  className="p-1.5 sm:p-2 rounded-full hover:bg-warm-cream transition-colors touch-manipulation min-h-[44px] min-w-[44px] sm:min-h-auto sm:min-w-auto flex items-center justify-center"
                  aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart 
                    className={`h-4 w-4 ${
                      isFavorited 
                        ? 'fill-coral-pink text-coral-pink' 
                        : 'text-sunset-medium hover:text-coral-pink'
                    }`} 
                  />
                </button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-2 sm:space-y-3 p-3 pt-0 sm:p-4 sm:pt-0">
          {terrace.photo_url && (
            <div className="relative h-28 sm:h-32 rounded-md overflow-hidden bg-warm-cream">
              <img
                src={terrace.photo_url}
                alt={terrace.name}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}


          <div className="flex flex-wrap gap-1 sm:gap-1.5">
            <Badge 
              variant="outline" 
              className="text-xs bg-peach-orange/10 border-peach-orange/30 text-peach-orange hover:bg-peach-orange/20 h-6 sm:h-auto"
            >
              {terrace.type}
            </Badge>
            
            {terrace.hasFood && (
              <Badge variant="outline" className="text-xs border-peach-orange/30 h-6 sm:h-auto">
                <Utensils className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                Food
              </Badge>
            )}
            
            {terrace.servesAlcohol && (
              <Badge variant="outline" className="text-xs border-peach-orange/30 h-6 sm:h-auto">
                <Wine className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                Drinks
              </Badge>
            )}
            
            {terrace.petFriendly && (
              <Badge variant="outline" className="text-xs border-peach-orange/30 h-6 sm:h-auto">
                <Dog className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-1" />
                Pets
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default TerraceCard;
